import Cookies from 'js-cookie';
import Vue from 'vue';
import Vuex from 'vuex';

Vue.use(Vuex);

const USER_KEY = Cookies.get('key');
const COOKIE_CONSENT = Cookies.get('cookieConsent');
const ENDPOINT = process.env.VUE_APP_ENDPOINT;

export default new Vuex.Store({
  state: {
    cookieConsent: COOKIE_CONSENT,
    key: USER_KEY,
    user: undefined,
    driver: undefined,
  },
  /* eslint-disable no-param-reassign */
  mutations: {
    consentReceived(state) {
      state.cookieConsent = true;
    },
    setKey(state, key) {
      state.key = key;
    },
    setUser(state, user) {
      state.user = user;
    },
    setDriver(state, driver) {
      state.driver = driver;
    },
  },
  /* eslint-enable no-param-reassign */
  actions: {
    // Handles user key validation and data retrieval
    // on session resume. eg, user refreshes or starts
    // new browser session
    login({ commit, dispatch, state }) {
      // Object destructuring
      const { key } = state;

      // eslint-disable-next-line no-unused-vars
      return new Promise((resolve, reject) => {
        // Resolve instantly if key is undefined
        // We can't login a guest!
        if (typeof key === 'undefined') return resolve();

        return dispatch('retrieveAuthedUser')
          .then((data) => {
            commit('setUser', data.user);
            Cookies.set('user', JSON.stringify(data.user));
          })
          .catch((err) => {
            if (err === 'Unauthorized') dispatch('logout');
          });
      });
    },
    // Handles user logout
    // Does not notify the API
    logout({ commit }) {
      // Remove auth cookie
      Cookies.remove('key');

      // Commit to current vuex store
      commit('setKey', undefined);
      commit('setUser', undefined);
      commit('setDriver', undefined);

      Cookies.remove('user');
      Cookies.remove('driver');
      Cookies.remove('key');
    },
    recordConsent({ commit }) {
      Cookies.set('cookieConsent', true);
      commit('consentReceived');
    },
    retrieveAuthedUser({ state }) {
      // Object destructuring
      const { key } = state;

      return new Promise((resolve, reject) => {
        // If not authenticated, reject promise
        if (typeof key === 'undefined') return reject(Error('Missing Auth Key!'));

        return fetch(`${ENDPOINT}accounts/whoami`, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache',
          headers: {
            Authorization: `Token ${key}`,
            'Content-Type': 'application/json',
          },
          redirect: 'error',
          referrer: 'no-referrer',
        })
          .then((response) => {
            if (response.ok) return resolve(response.json());

            return reject(response.statusText);
          });
      });
    },
    // Called when a user initially logs into the system
    // using the login form.
    // Payload is an object
    // {
    //   username: String,
    //   password: String,
    // };
    requestKey({ commit, dispatch }, payload) {
      // commit('setUser', undefined);
      // commit('setDriver', undefined);

      return new Promise((resolve, reject) => {
        fetch(`${ENDPOINT}rest-auth/login/`, {
          method: 'POST',
          mode: 'cors',
          cache: 'no-cache',
          headers: {
            'Content-Type': 'application/json',
          },
          redirect: 'error',
          referrer: 'no-referrer',
          body: JSON.stringify(payload),
        })
          .then(response => (response.ok ? response.json() : reject(Error('Invalid credentials'))))
          .then((body) => {
            // body is undefined if promise above is rejected
            if (typeof body === 'undefined') return resolve();

            // Store key as cookie and commit to active store
            dispatch('storeKey', body);
            // Retrieve user credentials
            dispatch('retrieveAuthedUser')
              // When finished, commit user object and discard driver object
              .then((data) => {
                commit('setUser', data.user);
                Cookies.set('user', JSON.stringify(data.user));
                if (data.driver) {
                  commit('setDriver', data.driver);
                  Cookies.set('driver', JSON.stringify(data.driver));
                }
              })
              .catch(err => reject(err));

            return resolve();
          });
      });
    },
    // Stores the payload into a cookie
    storeKey({ commit }, payload) {
      Cookies.set('key', payload.key);

      // Commit to current vuex store
      commit('setKey', payload.key);
    },
    updateUserFromCookie({ commit }) {
      commit('setKey', Cookies.get('key'));
      commit('setUser', JSON.parse(Cookies.get('user')));
    },
    updateDriverFromCookie({ commit }) {
      commit('setDriver', JSON.parse(Cookies.get('driver')));
    },
  },
  getters: {
    // Determines if the user is logged in
    // Does not mean that their key is valid, however
    authenticated: state => (typeof state.key !== 'undefined'),
    cookieConsent: state => (typeof state.cookieConsent !== 'undefined'),
    /* eslint-disable camelcase */
    fullName: (state) => {
      if (typeof state.user !== 'undefined') {
        // Object destructuring
        const { first_name, last_name } = state.user;
        return `${first_name} ${last_name}`;
      }

      return 'Guest';
    },
    userID: (state) => {
      if (typeof state.user !== 'undefined') {
        return state.user.id;
      }
      return 0;
    },
    userDetail: (state) => {
      if (typeof state.user !== 'undefined') {
        return state.user;
      }
      return undefined;
    },
    driverDetail: (state) => {
      if (typeof state.driver !== 'undefined') {
        return state.driver;
      }
      return undefined;
    },
    jwtToken: (state) => {
      if (typeof state.key !== 'undefined') {
        return state.key;
      }
      return undefined;
    },
    /* elint-enable camelcase */
  },
});
